import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../notification.hook";
import CmSheetDevelopmentIndicatorsService from "../../services/api/cmSheet/CmsheetIndicators.service";
import { RefreshReactQuery } from "../../constants/AppConstants";
import { useEffect } from "react";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import { UpdateCollaboratorCmSheetDevelopmentIndicatorsRequest } from "../../types/api/collaborators.type";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

export const CM_SHEET_DEVELOPMENTS_QUERY_KEY = "get-cm-sheet-development";

export const useCmSheetDevelopmentIndicators = (
  cmSheetId: number,
  enabled = true,
) => {
  const { notification } = useNotification();

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: [CM_SHEET_DEVELOPMENTS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetDevelopmentIndicatorsService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetDevelopmentIndicators = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateDevelopmentsIndicators, isPending: isUpdating } =
    useMutation({
      mutationFn: (
        body: UpdateCollaboratorCmSheetDevelopmentIndicatorsRequest,
      ) => CmSheetDevelopmentIndicatorsService.update(cmSheetId, body),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_DEVELOPMENTS_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
          }),
        ]);
        notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateDevelopmentsIndicators, isUpdating };
};
